import type { Optional } from '@izimi/utils';

import type { ApiContact } from '../contacts/types';
import type { ApiComponentType, ApiRequestQueryParamType, MaybeArray } from '../types';

export enum ApiDocumentCategory {
  All = 'all',
  Personal = 'personal',
  Household = 'household',
  Business = 'business',
  Medical = 'medical',
  RealEstate = 'real estate',
  Family = 'family',
  Property = 'property',
  Finance = 'finance',
}

export enum ApiDocumentSource {
  User = 'USER',
  Study = 'STUDY',
  Naban = 'NABAN',
  SSN = 'ENT_SSN',
  Shared = 'SHARED',
  Tuyeau = 'FPS_FINANCE',
  Vlabel = 'VLABEL',
}

export type SortParam = `${
  | 'name'
  | 'state'
  | 'modificationDate'
  | 'creationDate'
  | 'trashingDate'},${'asc' | 'desc'}`;

export interface GetDocumentsQueryParams extends Omit<
  ApiRequestQueryParamType<'searchDocuments'>,
  'sort' | 'states' | 'categories' | 'sources'
> {
  sort?: SortParam;
  states?: MaybeArray<ApiDocumentState>;
  categories?: MaybeArray<ApiDocumentCategory>;
  sources?: MaybeArray<ApiDocumentSource>;
}

interface BaseApiDocumentUser<Type extends 'CITIZEN' | 'STUDY'> {
  id: string;
  email: string;
  type: Type;
}

export interface ApiDocumentUserCitizen extends BaseApiDocumentUser<'CITIZEN'> {
  firstName: string;
  lastName: string;
}
export interface ApiDocumentUserStudy extends BaseApiDocumentUser<'STUDY'> {
  name: string;
}

export type ApiDocumentUser = ApiDocumentUserCitizen | ApiDocumentUserStudy;

export type ApiDocumentState = ApiComponentType<'DocumentState'>;

type BaseDocument = Omit<
  Required<ApiComponentType<'Document'>>,
  'categories' | 'source' | 'owner' | 'sharedWith' | 'afterDeathSelection'
>;

export interface ApiDocument extends BaseDocument {
  categories: ApiDocumentCategory[];
  source: ApiDocumentSource;
  owner: ApiDocumentUser;
  sharedWith: ApiDocumentUser[];
  afterDeathSelection: ApiDocumentAfterDeathSelection;
}

export interface ApiDocumentAfterDeathSelection {
  isSelectedForHeirs: boolean;
  selectedContacts?: Array<ApiDocumentUser>;
}

export interface ApiDocumentFilters {
  labels: string[];
  owners: ApiDocumentUser[];
  receivers: ApiDocumentUser[];
  sources: ApiDocumentSource[];
}

export type AcceptRejectSharedDocumentsResponse = {
  acceptedDocuments: ApiDocument[];
  rejectedDocuments: Array<{
    documentId: string;
    documentName: string;
  }>;
  failedDocuments: Array<{
    documentId: string;
    documentName: string;
  }>;
  addedContact?: ApiContact;
};

export interface GetDocumentsResponse {
  documents: ApiDocument[];
  total: number;
  numberUnseenDocuments?: number;
  uniqueFilterValues?: ApiDocumentFilters;
}

export interface ApiNabanDocumentMetadata {
  office: string;
  nabanDocumentId: string;
  fileName: string;
  type: string;
  date: string;
  deedDate: string;
  deedReference: string;
  deedRepertoriumNumber: string;
  penHolder: string;
  originalDocumentName: string;
  documentType: string;
  deedType: string;
}

export interface ApiVlabelDocumentMetadataRaw {
  DOCUMENTTYPE?: string;
  BELASTINGFAMILIE?: string;
  OGMNUMMER?: string;
  EXPEDITIENUMMER?: string;
  REPERTORIUMNUMMER?: string;
  ERFLATER?: string;
}

export interface ApiVlabelDocumentMetadata {
  documentType?: string;
  taxFamily?: string;
  ogmNumber?: string;
  expeditionNumber?: string;
  repertoryNumber?: string;
  heir?: string;
}

export type DocumentsFilterParams = Optional<GetDocumentsQueryParams, 'offset' | 'limit'>;
